import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/button/button'
import { Spinner } from '@/components/spinner/spinner'
import { Typography } from '@/components/typography/typography'
import { useBookingCompletion } from '@/features/booking'
import {
	Reservation,
	UpdateReservationParams,
	useReservation,
	useUpdateReservation,
} from '@/features/reservation'
import { cn } from '@/libs/tw'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { getAdvertisementCode } from '@/utils/getAdvertisementCode'
import { translate } from '@/utils/translator'

import { useValidationContext } from '../hooks/useValidationContext'
import { FormValues, bookingForValues, createFormSchema } from '../schema'
import { GuestDataForm } from './forms/guestDataForm'
import { GuestDataSummary } from './guestDataSummary'

const mapFormToReservation = (form: FormValues, id: string): UpdateReservationParams => ({
	reservationId: id,
	newsletterRegistration: form.newsLetter,
	advertisementCode: getAdvertisementCode(),
	customer: {
		firstName: form.name,
		lastName: form.surname,
		salutation: null,
		title: null,
		email: form.email,
		phone: form.phoneNumber,
		address: {
			addressCountry: form.country,
			addressLocality: form.city,
			postalCode: form.postCode,
			streetAddress: `${form.street} ${form.streetNumber}`,
		},
		isSufficient: true,
	},
	comment: form.annotation,
	guests: form.room
		.map((guests) =>
			guests.map((guest) => ({
				id: guest.id,
				age: guest.age ?? null,
				firstName: guest.name,
				lastName: guest.surname,
				salutation: null,
				isSufficient: true,
			}))
		)
		.flat(),
	...(form.employeesPromotion && {
		employeeBookingEligibility: {
			employeeCompany: form.employeesPromotion.company,
			employeeFamilyRelation: form.employeesPromotion.companyRelationship,
			employeeName: form.employeesPromotion.employeeName,
		},
	}),
})

const mapReservationToForm = (reservation: Reservation['reservation']): FormValues => ({
	bookingFor:
		reservation.customer.firstName === reservation.roomStays[0].guests[0].firstName &&
		reservation.customer.lastName === reservation.roomStays[0].guests[0].lastName
			? 'me'
			: 'other',
	email: reservation.customer.email ?? '',
	phoneNumber: reservation.customer.phone!,
	name: reservation.customer.firstName,
	surname: reservation.customer.lastName,
	country: reservation.customer.address!.addressCountry,
	postCode: reservation.customer.address!.postalCode,
	city: reservation.customer.address!.addressLocality,
	street: reservation.customer.address!.streetAddress.split(' ')[0],
	streetNumber: reservation.customer.address!.streetAddress.split(' ')[1],
	annotation: reservation.comment,
	room: reservation.roomStays.map((room) =>
		room.guests.map((guest) => ({
			salutation: guest.salutation,
			age: guest.age,
			name: guest.firstName,
			surname: guest.lastName,
			id: guest.id,
		}))
	),
	...(reservation.employeeBookingDetails?.employeeBookingEligibility && {
		employeesPromotion: {
			company:
				reservation.employeeBookingDetails?.employeeBookingEligibility.employeeCompany ??
				'',
			companyRelationship:
				reservation.employeeBookingDetails?.employeeBookingEligibility
					.employeeFamilyRelation ?? '',
			employeeName:
				reservation.employeeBookingDetails?.employeeBookingEligibility.employeeName ?? '',
		},
	}),
})

export const GuestDataContent = () => {
	const language = useAppConfigStore.use.language()
	const location = useCartStore.use.location()
	const setNewsletter = useCartStore.use.setNewsletter()
	const { handleBookingCompletion } = useBookingCompletion()
	const { country } = useValidationContext()

	const { data: reservation, isPending: loadingReservation } = useReservation()
	const { mutate: updateReservation, isPending } = useUpdateReservation(handleBookingCompletion)

	const methods = useForm<FormValues>({
		resolver: zodResolver(createFormSchema(country)),
		defaultValues: {
			bookingFor: bookingForValues.ME,
			newsLetter: false,
		},
	})

	useEffect(() => {
		if (
			!reservation ||
			(reservation?.customer.firstName === '--unknown--' &&
				reservation?.customer.lastName === '--unknown--')
		) {
			return
		}

		methods.reset(mapReservationToForm(reservation))
	}, [language, methods, reservation])

	const { handleSubmit } = methods

	const showContent = reservation?.reservationId && location && !loadingReservation

	return (
		<FormProvider {...methods}>
			{showContent ? (
				<>
					<Typography
						style={'p-s'}
						element="p"
						className="mb-8 text-userData-checkBox-secondaryMobile"
					>
						{`* = ${translate('guest-data-mandatory')}`}
					</Typography>
					<GuestDataForm />
					<GuestDataSummary showLegal />
					<Button
						style="transparent"
						onClick={handleSubmit((data) => {
							setNewsletter(Boolean(data.newsLetter))
							return updateReservation({
								reservation: mapFormToReservation(data, reservation.reservationId),
								hotelCode: location.hotelCode,
							})
						})}
						className={cn(
							'mt-24 w-full bg-userData-button text-userData-buttonText md:w-1/4'
						)}
					>
						{isPending ? <Spinner /> : translate('room-selection-submit')}
					</Button>
				</>
			) : (
				<Spinner />
			)}
		</FormProvider>
	)
}
